import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  User, Lock, Camera, Save, RefreshCw, Eye, EyeOff,
  Shield, Mail, Bell, Moon, Sun, Globe
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeProfile() {
  const { theme, setTheme, user, refreshUser } = useOutletContext();
  const isDark = theme === 'dark';

  // Profile data
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  // Password change
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Preferences
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    ticketAlerts: true,
    dailyDigest: false,
    language: "en",
    timezone: "Europe/London",
    currency: "GBP"
  });
  const [savingPreferences, setSavingPreferences] = useState(false);

  // Timezone options
  const timezones = [
    { value: "Europe/London", label: "London (GMT/BST)" },
    { value: "America/New_York", label: "New York (EST/EDT)" },
    { value: "America/Los_Angeles", label: "Los Angeles (PST/PDT)" },
    { value: "America/Chicago", label: "Chicago (CST/CDT)" },
    { value: "Europe/Paris", label: "Paris (CET/CEST)" },
    { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Asia/Shanghai", label: "Shanghai (CST)" },
    { value: "Asia/Kolkata", label: "Mumbai (IST)" },
    { value: "Asia/Dubai", label: "Dubai (GST)" },
    { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
    { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
  ];

  // Currency options  
  const currencies = [
    { value: "GBP", label: "£ British Pound (GBP)", symbol: "£" },
    { value: "USD", label: "$ US Dollar (USD)", symbol: "$" },
    { value: "EUR", label: "€ Euro (EUR)", symbol: "€" },
    { value: "INR", label: "₹ Indian Rupee (INR)", symbol: "₹" },
    { value: "AUD", label: "A$ Australian Dollar (AUD)", symbol: "A$" },
    { value: "CAD", label: "C$ Canadian Dollar (CAD)", symbol: "C$" },
    { value: "JPY", label: "¥ Japanese Yen (JPY)", symbol: "¥" },
    { value: "CNY", label: "¥ Chinese Yuan (CNY)", symbol: "¥" },
  ];

  useEffect(() => {
    fetchProfile();
  }, []);
const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/internal/profile`, { withCredentials: true });
      setProfile(res.data);
      if (res.data.preferences) {
        setPreferences(prev => ({ ...prev, ...res.data.preferences }));
      }
    } catch (error) {
      // Fallback to current user data
      if (user) {
        setProfile(user);
      } else {
        toast.error("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/internal/profile/avatar`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile(prev => ({ ...prev, profile_image: res.data.url }));
      toast.success("Profile image updated");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordForm.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/internal/profile/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, { withCredentials: true });

      toast.success("Password changed successfully");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      await axios.put(`${API}/internal/profile/preferences`, preferences, { withCredentials: true });
      toast.success("Preferences saved");
    } catch (error) {
      // Handle different error formats to prevent "Objects are not valid as React child" errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Failed to save preferences";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSavingPreferences(false);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('backoffice_theme', newTheme);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const displayProfile = profile || user;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Profile Settings
        </h1>
        <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Manage your account settings and preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className={isDark ? 'bg-slate-800' : 'bg-gray-100'}>
          <TabsTrigger value="profile" className="gap-2">
            <User className="w-4 h-4" /> Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Lock className="w-4 h-4" /> Security
          </TabsTrigger>
          <TabsTrigger value="preferences" className="gap-2">
            <Bell className="w-4 h-4" /> Preferences
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Profile Information
            </h2>

            {/* Profile Photo */}
            <div className="flex items-center gap-6 mb-8">
              <div className="relative">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold ${
                  isDark ? 'bg-violet-600' : 'bg-violet-500'
                } text-white overflow-hidden`}>
                  {displayProfile?.profile_image ? (
                    <img 
                      src={displayProfile.profile_image} 
                      alt={displayProfile.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    displayProfile?.name?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
                <label className={`absolute bottom-0 right-0 p-2 rounded-full cursor-pointer ${
                  isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}>
                  <Camera className="w-4 h-4" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </label>
                {uploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <RefreshCw className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Profile Photo
                </p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Click the camera icon to upload a new photo
                </p>
              </div>
            </div>

            {/* Profile Details (Read-only - managed by Super Admin) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Full Name</Label>
                <div className={`mt-1 p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}>{displayProfile?.name || '-'}</p>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Contact Super Admin to change
                </p>
              </div>

              <div>
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Email Address</Label>
                <div className={`mt-1 p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}>{displayProfile?.email || '-'}</p>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Contact Super Admin to change
                </p>
              </div>

              <div>
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Role</Label>
                <div className={`mt-1 p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}>
                    {displayProfile?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}
                  </p>
                </div>
              </div>

              <div>
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Department</Label>
                <div className={`mt-1 p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={isDark ? 'text-white' : 'text-gray-900'}>{displayProfile?.department || '-'}</p>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Change Password
            </h2>

            <div className="space-y-4 max-w-md">
              <div>
                <Label>Current Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-gray-400'}`}
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-gray-400'}`}
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Minimum 8 characters
                </p>
              </div>

              <div>
                <Label>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-gray-400'}`}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button onClick={handleChangePassword} disabled={changingPassword} className="mt-4">
                {changingPassword && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>

            {/* Security Info */}
            <div className={`mt-8 pt-6 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Account Security
              </h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <Shield className={`w-5 h-5 ${displayProfile?.email_verified ? 'text-green-500' : 'text-amber-500'}`} />
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Verification</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {displayProfile?.email_verified ? 'Your email is verified' : 'Email not verified'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    displayProfile?.email_verified 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {displayProfile?.email_verified ? 'Verified' : 'Pending'}
                  </span>
                </div>

                <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <Lock className={`w-5 h-5 ${displayProfile?.two_factor_enabled ? 'text-green-500' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Two-Factor Authentication</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {displayProfile?.two_factor_enabled ? 'Enabled' : 'Not enabled'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    displayProfile?.two_factor_enabled 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {displayProfile?.two_factor_enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Preferences
            </h2>

            <div className="space-y-6 max-w-md">
              {/* Theme */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isDark ? <Moon className="w-5 h-5 text-violet-400" /> : <Sun className="w-5 h-5 text-amber-500" />}
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Dark Mode</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Toggle dark/light theme
                    </p>
                  </div>
                </div>
                <Switch checked={isDark} onCheckedChange={toggleTheme} />
              </div>

              {/* Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Notifications</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Receive important updates via email
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={preferences.emailNotifications} 
                  onCheckedChange={(checked) => setPreferences({ ...preferences, emailNotifications: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Ticket Alerts</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Get notified on new/assigned tickets
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={preferences.ticketAlerts} 
                  onCheckedChange={(checked) => setPreferences({ ...preferences, ticketAlerts: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Language</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Select your preferred language
                    </p>
                  </div>
                </div>
                <Select value={preferences.language} onValueChange={(v) => setPreferences({ ...preferences, language: v })}>
                  <SelectTrigger className={`w-32 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Timezone Setting */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Timezone</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Set your local timezone for accurate times
                    </p>
                  </div>
                </div>
                <Select value={preferences.timezone || "Europe/London"} onValueChange={(v) => setPreferences({ ...preferences, timezone: v })}>
                  <SelectTrigger className={`w-48 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Currency Setting */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Currency</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Default currency for pricing display
                    </p>
                  </div>
                </div>
                <Select value={preferences.currency || "GBP"} onValueChange={(v) => setPreferences({ ...preferences, currency: v })}>
                  <SelectTrigger className={`w-48 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>{curr.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleSavePreferences} disabled={savingPreferences} className="mt-4">
                {savingPreferences && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
